import CloseIcon from "@mui/icons-material/Close";
import {LoadingButton} from "@mui/lab";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Link,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
    useMediaQuery
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {googleFontFromLink, googleFontName, googleFontsCss, useFont} from "../fonts";

/** Free Google fonts that suit a hotel, with German and Italian letters. */
const RECOMMENDED_FONTS = [
    {family: 'Lora', weights: '400;600', mood: 'Warm'},
    {family: 'Playfair Display', weights: '400;600', mood: 'Elegant'},
    {family: 'Cormorant Garamond', weights: '500;600', mood: 'Luxurious'},
    {family: 'Montserrat', weights: '400;600', mood: 'Modern'},
    {family: 'Nunito Sans', weights: '400;600', mood: 'Friendly'},
] as const;

const SOMETHING_ELSE = 'SOMETHING_ELSE';

// One stylesheet for the cards' previews, loaded while the dialog is open
const PREVIEW_CSS = `https://fonts.googleapis.com/css2?${RECOMMENDED_FONTS
    .map(font => `family=${font.family.replace(/ /g, '+')}:wght@${font.weights}`)
    .join('&')}&display=swap`;

const TITLE_ID = 'google-fonts-title';
const EMBED_PLACEHOLDER = '<link href="https://fonts.googleapis.com/css2?family=Raleway&display=swap" rel="stylesheet">';
const CHECK_DELAY_MS = 400;

// 'empty': Use font was pressed with nothing pasted
type LinkCheck =
    | {status: 'idle' | 'checking' | 'empty'}
    | {status: 'found', family: string, stylesheet: string}
    | {status: 'invalid' | 'missing'};

/** Where an opening starts: the saved font - a recommended one, else "Something else" with its stylesheet link. */
const startOf = (fontUrl?: string | null): {picked: string, link: string, check: LinkCheck} => {
    const current = googleFontName(fontUrl);
    const recommended = RECOMMENDED_FONTS.find(font => font.family === current);
    if (current && !recommended && fontUrl) {
        return {
            picked: SOMETHING_ELSE,
            link: fontUrl,
            check: {status: 'found', family: current, stylesheet: fontUrl},
        };
    }
    return {picked: recommended?.family ?? RECOMMENDED_FONTS[0].family, link: '', check: {status: 'idle'}};
};

/**
 * The pasted font's stylesheet, if Google Fonts has the font: a pasted stylesheet as it is, else the
 * font in the page's two weights - or in its only one, as Google refuses weights a font doesn't have.
 */
const findStylesheet = async (text: string): Promise<LinkCheck> => {
    const font = googleFontFromLink(text);
    if (!font) return {status: 'invalid'};

    const candidates = font.stylesheet ? [font.stylesheet] : [googleFontsCss(font.family, '400;600'), googleFontsCss(font.family)];
    for (const stylesheet of candidates) {
        try {
            if ((await fetch(stylesheet)).ok) return {status: 'found', family: font.family, stylesheet};
        } catch {
            // Unreachable counts as not found
        }
    }
    return {status: 'missing'};
};

/** One radio card, laid out like the dashboard's RadioCardPicker: the label, a second line, the radio on the right. */
const FontOption = ({value, selected, label, description}: {value: string, selected: boolean, label: React.ReactNode, description: string}) => (
    <Box
        component={'label'}
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            minHeight: 60,
            px: 2,
            py: 1.25,
            border: '1px solid',
            borderColor: selected ? 'text.primary' : 'divider',
            borderRadius: 1,
            boxShadow: selected ? 'inset 0 0 0 1px currentColor' : 'none',
            cursor: 'pointer',
            transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
            '&:hover': {bgcolor: 'action.hover', borderColor: selected ? 'text.primary' : 'text.secondary'},
        }}
    >
        <Stack minWidth={0} flex={1}>
            {label}
            <Typography variant={'body2'} color={'text.secondary'}>{description}</Typography>
        </Stack>
        <Radio value={value} checked={selected} sx={{p: 0, color: 'divider', '&.Mui-checked': {color: 'text.primary'}}}/>
    </Box>
);

interface Props {
    open: boolean
    /** The saved Google Fonts link, to start from. */
    fontUrl?: string | null
    onClose: () => void
    /** The chosen font's stylesheet link. */
    onUse: (fontUrl: string) => void
}

/** Google Fonts: a recommended font, or "Something else" from the link of a font's page. */
const GoogleFontDialog = ({open, fontUrl, onClose, onUse}: Props) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // The dialog unmounts its content when closed, so each opening starts afresh
    return (
        <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth fullScreen={fullScreen} aria-labelledby={TITLE_ID}>
            <FontPicker fontUrl={fontUrl} onClose={onClose} onUse={onUse}/>
        </Dialog>
    );
};

const FontPicker = ({fontUrl, onClose, onUse}: Omit<Props, 'open'>) => {
    const {t} = useTranslation();
    const [start] = useState(() => startOf(fontUrl));
    const [picked, setPicked] = useState(start.picked);
    const [link, setLink] = useState(start.link);
    const [check, setCheck] = useState<LinkCheck>(start.check);
    const checkRef = useRef(0);

    useFont('GOOGLE', PREVIEW_CSS);
    useFont('GOOGLE', check.status === 'found' ? check.stylesheet : undefined);

    const changeLink = (text: string) => {
        setLink(text);
        const run = ++checkRef.current;
        if (!text.trim()) {
            setCheck({status: 'idle'});
            return;
        }
        setCheck({status: 'checking'});
        window.setTimeout(async () => {
            if (run !== checkRef.current) return;
            const result = await findStylesheet(text);
            if (run === checkRef.current) setCheck(result);
        }, CHECK_DELAY_MS);
    };

    const recommended = RECOMMENDED_FONTS.find(font => font.family === picked);
    const previewFamily = recommended?.family ?? (check.status === 'found' ? check.family : undefined);
    const linkError = check.status === 'empty' ? t('Paste the font\'s embed code first.')
        : check.status === 'invalid' ? t('This isn\'t embed code from Google Fonts.')
            : check.status === 'missing' ? t('Google Fonts has no font at this link.') : undefined;

    const use = () => {
        if (recommended) onUse(googleFontsCss(recommended.family, recommended.weights));
        else if (check.status === 'found') onUse(check.stylesheet);
        else if (!link.trim()) setCheck({status: 'empty'});
    };

    return (
        <>
            <DialogTitle component={'div'} sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                    <Typography component={'h2'} id={TITLE_ID} variant={'h6'} fontWeight={600} noWrap>
                        Google Fonts
                    </Typography>
                    <IconButton onClick={onClose} size={'small'} aria-label={t('Close')}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{px: 3, py: 3}}>
                <RadioGroup
                    value={picked}
                    onChange={event => setPicked(event.target.value)}
                    aria-label={'Google Fonts'}
                    sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 1}}
                >
                    {RECOMMENDED_FONTS.map(font => (
                        <FontOption
                            key={font.family}
                            value={font.family}
                            selected={picked === font.family}
                            label={<Typography noWrap sx={{fontFamily: `"${font.family}", serif`, fontSize: 18, lineHeight: 1.4}}>{font.family}</Typography>}
                            description={t(font.mood)}
                        />
                    ))}
                    <FontOption
                        value={SOMETHING_ELSE}
                        selected={picked === SOMETHING_ELSE}
                        label={<Typography variant={'body1'} sx={{fontWeight: 500}}>{t('Something else')}</Typography>}
                        description={t('Any font from Google Fonts')}
                    />
                </RadioGroup>

                {picked === SOMETHING_ELSE && (
                    <Stack gap={1.5} sx={{mt: 2}}>
                        <Alert severity={'info'} variant={'outlined'}>
                            {t('Open the font on')}{' '}
                            <Link href={'https://fonts.google.com'} target={'_blank'} rel={'noopener noreferrer'}>Google Fonts</Link>,{' '}
                            {t('copy its embed code (Get font, then Get embed code) and paste it here.')}
                        </Alert>
                        {/* Takes the embed code, a stylesheet link or the font's page link - googleFontFromLink reads all three */}
                        <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={8}
                            autoFocus
                            value={link}
                            placeholder={EMBED_PLACEHOLDER}
                            error={!!linkError}
                            helperText={linkError}
                            inputProps={{'aria-label': t('Embed code'), spellCheck: false}}
                            onChange={event => changeLink(event.target.value)}
                            sx={{'& textarea': {fontFamily: 'monospace', fontSize: 14, lineHeight: 1.5}}}
                            InputProps={{
                                endAdornment: check.status === 'checking'
                                    ? <InputAdornment position={'end'} sx={{alignSelf: 'flex-start', mt: 1.5}}><CircularProgress size={16}/></InputAdornment>
                                    : undefined,
                            }}
                        />
                    </Stack>
                )}

                <Box sx={{mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover'}}>
                    <Typography
                        component={'div'}
                        sx={{fontFamily: previewFamily ? `"${previewFamily}", serif` : undefined, fontSize: 26, lineHeight: 1.3, fontWeight: previewFamily ? 600 : 400}}
                    >
                        {t('A moment for you')}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <LoadingButton variant={'contained'} loading={!recommended && check.status === 'checking'} onClick={use}>
                    {t('Use font')}
                </LoadingButton>
            </DialogActions>
        </>
    );
};

export default GoogleFontDialog;
